import { useMemo } from "react";

import { RVSideNavLayout, type PageSubNavItem } from "@regal-voice/ui";
import { usePageAvailability } from "App/routes/Hooks/usePageAvilability/usePageAvailability";
import { TOP_LEVEL_PAGES_TITLE_MAP } from "App/routes/pageNames";
import { AppPagePaths } from "App/routes/paths";
import { CollapsiblePanel } from "Utils/CollapsiblePanels";
import { useKnownPanels } from "Utils/CollapsiblePanels/knownPanels";

import { CONVO_INTEL_SUB_NAV_TITLES, CONVO_INTEL_SUB_PAGE_TITLES_PATCH } from "../../convoIntelMetadata";
import { convoIntelPathResources } from "../../convoIntelPaths";

export function ConvoIntelSubNav(): JSX.Element {
    const { convoIntelPages } = usePageAvailability();
    const {
        showRecordings,
        showObservability,
        showMoments,
        showCustomAnalysis,
        showScorecards,
        showInsights,
        showAgentChats,
    } = convoIntelPages;

    const items: PageSubNavItem[] = useMemo(() => {
        const navItems: PageSubNavItem[] = [];

        if (showRecordings) {
            navItems.push({
                key: convoIntelPathResources.recordings,
                title: CONVO_INTEL_SUB_NAV_TITLES[convoIntelPathResources.recordings],
                href: `/${convoIntelPathResources.recordings}`,
            });
        }

        if (showAgentChats) {
            navItems.push({
                key: convoIntelPathResources.agentChats,
                title: CONVO_INTEL_SUB_NAV_TITLES[convoIntelPathResources.agentChats],
                href: `/${convoIntelPathResources.agentChats}`,
            });
        }

        if (showObservability) {
            navItems.push({
                key: convoIntelPathResources.observability,
                title: CONVO_INTEL_SUB_NAV_TITLES[convoIntelPathResources.observability],
                href: `/${convoIntelPathResources.observability}`,
            });
        }

        if (showMoments) {
            navItems.push({
                key: convoIntelPathResources.moments,
                title: CONVO_INTEL_SUB_NAV_TITLES[convoIntelPathResources.moments],
                href: `/${convoIntelPathResources.moments}`,
            });
        }

        if (showCustomAnalysis) {
            navItems.push({
                key: convoIntelPathResources.customAnalysis,
                title: CONVO_INTEL_SUB_NAV_TITLES[convoIntelPathResources.customAnalysis],
                href: `/${convoIntelPathResources.customAnalysis}`,
            });
        }

        if (showScorecards) {
            navItems.push({
                key: convoIntelPathResources.scorecards,
                title: CONVO_INTEL_SUB_PAGE_TITLES_PATCH[convoIntelPathResources.scorecards],
                href: `/${convoIntelPathResources.scorecards}`,
                tabs: [`/${convoIntelPathResources.scorecardEdit}`],
            });
        }

        if (showInsights) {
            navItems.push({
                key: convoIntelPathResources.insights,
                title: CONVO_INTEL_SUB_PAGE_TITLES_PATCH[convoIntelPathResources.insights],
                href: `/${convoIntelPathResources.insights}`,
            });
        }

        return navItems;
    }, [
        showRecordings,
        showObservability,
        showMoments,
        showCustomAnalysis,
        showScorecards,
        showInsights,
        showAgentChats,
    ]);

    const { convoIntel } = useKnownPanels();

    return (
        <CollapsiblePanel panelName={convoIntel.name} expandedWidth={convoIntel.width}>
            <RVSideNavLayout
                sidenavTitle={TOP_LEVEL_PAGES_TITLE_MAP[AppPagePaths.convoIntel]}
                items={items}
                basePath={AppPagePaths.convoIntel}
                hasSubPages
            />
        </CollapsiblePanel>
    );
}
